// src/pages/AboutUs/AboutUsPage.jsx
import React, { lazy, Suspense, useEffect } from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet-async";

import AboutUsBanner from "../../component/AboutUs/AboutUsBanner/AboutUsBanner";
const CompanyOverview   = lazy(() => import("../../component/AboutUs/CompanyOverview/CompanyOverview"));
const Timeline          = lazy(() => import("../../component/AboutUs/MissionVisionGoals/Timeline"));
const OfferSection      = lazy(() => import("../../component/AboutUs/WhatWeOffer/OfferSection"));
const FeaturesSection   = lazy(() => import("../../component/AboutUs/WhyNextech/FeaturesSection"));
const OurTeam           = lazy(() => import("../../component/AboutUs/OurTeams/OurTeam"));
const ContactForm       = lazy(() => import("../../component/ContactUs/ContactUsForm/ContactForm"));
const FloatingContactUs = lazy(() => import("../../component/floting_contact/FloatingContactUs"));

/* =========================
   Solid Layout Styles
   ========================= */
const PageWrap = styled.main`
  font-family: "Poppins", sans-serif;
  background: #ffffff; /* Clean solid white background */
`;

/* Banner shell */
const ParallaxBanner = styled.section`
  position: relative;
  isolation: isolate; 
  overflow: hidden;
`;

/* Light parallax blobs behind the banner */
const BannerParallaxLayer = styled.div`
  position: absolute; inset: -10% -10% auto -10%;
  height: 60%;
  z-index: 0;
  background:
    radial-gradient(40% 30% at 20% 70%, rgba(0,146,255,.10), transparent 70%),
    radial-gradient(36% 28% at 80% 30%, rgba(0,198,255,.10), transparent 70%);
  transform: translateY(var(--py, 0px));
  will-change: transform;
`;

/* ================ */
/*  Utility Hooks   */
/* ================ */
const useScrollToTop = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
};

/* Parallax for the banner */
const useParallax = () => {
  useEffect(() => {
    const items = Array.from(document.querySelectorAll("[data-parallax]"));
    if (!items.length) return;
    let raf = null;

    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        const y = window.scrollY || window.pageYOffset || 0;
        items.forEach((el) => {
          const speed = parseFloat(el.getAttribute("data-parallax")) || 0.2;
          el.style.setProperty("--py", `${Math.round(y * speed)}px`);
        });
        raf = null;
      });
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
};

/* ========================= */
/*         Component         */
/* ========================= */
const AboutUsPage = () => {
  useScrollToTop();
  useParallax();

  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "NexTech PLM Solutions",
    "url": "https://www.nextechplm.com/about",
    "logo": "https://www.nextechplm.com/static/logo.png",
    "sameAs": ["https://www.linkedin.com/company/nextech-plm-solutions"],
    "contactPoint": [{
      "@type": "ContactPoint",
      "telephone": "+91-XXXXXXXXXX",
      "contactType": "sales",
      "areaServed": "IN",
      "availableLanguage": ["en"]
    }]
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home",     "item": "https://www.nextechplm.com/" },
      { "@type": "ListItem", "position": 2, "name": "About Us", "item": "https://www.nextechplm.com/about" }
    ]
  };

  return (
    <PageWrap>
      <Helmet>
        <title>About Us | NexTech PLM Solutions — Teamcenter & Engineering Services</title>
        <meta
          name="description"
          content="NexTech PLM Solutions helps manufacturers accelerate digital transformation with Teamcenter implementations, CAD/CAM/CAE services, integrations, and managed support. Meet our team, vision, and delivery model."
        />
        <meta name="keywords" content="about NexTech PLM, PLM company Pune, Teamcenter experts India, NexTech PLM team, PLM consulting company, engineering services Pune, digital transformation PLM" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <link rel="canonical" href="https://nextechplm.com/about" />

        <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      {/* ===== Banner ===== */}
      <ParallaxBanner>
        <BannerParallaxLayer data-parallax="0.15" aria-hidden="true" />
        <AboutUsBanner />
      </ParallaxBanner>

      <Suspense fallback={<div className="container py-5">Loading…</div>}>

        {/* Sections are now completely free of the glass containers! */}
        <div id="overview">
          <CompanyOverview />
        </div>

        <div id="offer">
          <OfferSection />
        </div>

        <div id="mission">
          <Timeline />
        </div>

        <div id="why-nextech">
          <FeaturesSection />
        </div>

        <div id="team">
          <OurTeam />
        </div>

        <div id="contact">
          <ContactForm />
        </div>

        <FloatingContactUs />
      </Suspense>
    </PageWrap>
  );
};

export default AboutUsPage;